// Pinning and unpinning, and what each writes into the project's presets.
import { getProperty as getProp } from 'dot-prop'
import { produce } from 'immer'
import conform from '~/lib/conformSettings'
import { objectPinKeys, pinRefusal, subtreePins } from '~/lib/livePins'
import { parseLivePath, sourceParamPath, sourceParams, vocabPinId } from '~/lib/params'
import { pinKeysOf, pinValue } from '~/lib/pinLayer'
import { isShaderSource } from '~/lib/shaders'
import { fillSourcesOf } from '~/lib/sourceFill'
import { selectIsTemplate, selectProjectId } from '~/store/app/selectors'
import { gatherSubtreeIds, recursivelyDeleteObjects } from '~/store/live/utils'
import { pushHistory, recordPresets } from '~/store/middleware/history'
import { isDesktop } from '~/store/middleware/pinsSync'
import { addLiveProp, removeLiveProp } from '~/store/playback'
import { writePreset } from '~/store/playback/presetWrites'
import { selectLiveProps, selectPresets } from '~/store/playback/selectors'

const logger = console.scope('pins', true)

const MAX_DEPTH = 16

const pinnedIds = s => new Set(Object.keys(s.playback.pins?.objects ?? {}))

const rootOf = (objects, id) => {
	let cur = objects?.[id]
	for (let d = 0; cur && (cur.parentId ?? 'root') !== 'root' && d < MAX_DEPTH; d++) cur = objects[cur.parentId]
	return cur?.id
}

const writesPresets = s => isDesktop(s) && !selectIsTemplate(s)

// The step's history entry takes each preset as it was, so one undo restores it.
const writePresets = (dispatch, s, edit) => {
	const projectId = selectProjectId(s)
	const writes = []
	for (const preset of selectPresets(s)) {
		if (!preset?.settings) continue
		const edited = produce(preset.settings, edit)
		if (edited === preset.settings) continue
		// A bake of an at-initial value conforms back to what is stored.
		const settings = conform(edited)
		if (JSON.stringify(settings) !== JSON.stringify(conform(preset.settings))) writes.push([preset, settings])
	}
	if (!writes.length) return
	dispatch(recordPresets(Object.fromEntries(writes.map(([p]) => [p.id, p.settings]))))
	for (const [{ id }, settings] of writes) writePreset(dispatch, { projectId, id, settings })
}

const step = (dispatch, getState) => {
	if (!getState().history.batch.isActive) dispatch(pushHistory)
}

/** Pin `keys`. An object prop pins existence of its root-level object's whole subtree, which lands in a band: refused otherwise. */
export const pinKeys =
	(keys, { history = true } = {}) =>
	(dispatch, getState) => {
		const s = getState()
		const list = [keys].flat()
		const pinned = pinnedIds(s)
		const objects = s.live.objects
		for (const key of [...list]) {
			const p = parseLivePath(key)
			if (p?.scope !== 'object') continue
			const root = rootOf(objects, p.id)
			if (pinned.has(root)) continue
			const why = pinRefusal(objects, pinned, root)
			if (why) {
				logger.warn('pin refused', key, why)
				return false
			}
			list.push(...objectPinKeys(objects, root, 'existence'))
		}
		if (history) step(dispatch, getState)
		dispatch(addLiveProp([...new Set(list)]))
		return true
	}

const valueOf = (s, key) => {
	const v = s.playback.pins_loaded ? getProp(s.live, key) : undefined
	return v !== undefined ? v : pinValue(s.playback.pins, key)
}

const bake = (d, key, v, all) => {
	const id = vocabPinId(key)
	if (id) {
		// Into every shader the preset shows that declares the id; conform clamps it to each one's range.
		for (const source of fillSourcesOf(d.objects))
			if (isShaderSource(source) && sourceParams(source)?.[id]) bake(d, sourceParamPath(source, id), v, all)
		return
	}
	const p = parseLivePath(key)
	if (!p || (v === undefined && !all)) return
	if (v === undefined) {
		const bag =
			p.scope === 'effect'
				? d[p.effect]
				: p.scope === 'source'
					? d.source_settings?.[p.source]
					: d.objects?.[p.id]
		if (bag && p.key in bag) delete bag[p.key]
		return
	}
	let bag
	if (p.scope === 'effect') bag = d[p.effect] ??= {}
	else if (p.scope === 'source') {
		if (!d.source_settings?.[p.source] && !fillSourcesOf(d.objects).has(p.source)) return
		bag = (d.source_settings ??= {})[p.source] ??= {}
	} else bag = d.objects?.[p.id]
	if (!bag || (!all && p.key in bag)) return
	bag[p.key] = structuredClone(v)
}

/** Unpin `keys`. Each preset keeps its own value where it carries one and takes the current one where it does not; `all` overwrites everywhere. A prop bakes only into presets carrying its object. */
export const unpinKeys =
	(keys, { all = false, history = true } = {}) =>
	(dispatch, getState) => {
		const s = getState()
		const before = selectLiveProps(s)
		if (history) step(dispatch, getState)
		dispatch(removeLiveProp(keys))
		const after = new Set(selectLiveProps(getState()))
		const gone = before.filter(k => !after.has(k) && !parseLivePath(k)?.existence)
		if (!gone.length || !writesPresets(s)) return
		const values = gone.map(k => [k, valueOf(s, k)])
		writePresets(dispatch, s, d => {
			for (const [k, v] of values) bake(d, k, v, all)
		})
	}

export const togglePin = key => (dispatch, getState) =>
	pinKeysOf(getState().playback.pins).includes(key) ? dispatch(unpinKeys([key])) : dispatch(pinKeys([key]))

/** The layer row's menu: pin `id`'s subtree as `mode` (`PIN_MODES`), or unpin it (null). One undo step; a prop leaving the pin bakes as `unpinKeys` does. */
export const setObjectPinMode = (id, mode) => (dispatch, getState) => {
	const s = getState()
	const objects = s.live.objects
	const held = subtreePins(objects, selectLiveProps(s), id)
	const want = new Set(mode ? objectPinKeys(objects, id, mode) : [])
	const drop = mode ? held.filter(k => !want.has(k)) : held.filter(k => parseLivePath(k)?.existence)
	const add = [...want].filter(k => !held.includes(k))
	// Refused before the undo step, so a refusal leaves none.
	const why = mode && !held.length && pinRefusal(objects, pinnedIds(s), id)
	if (why) {
		logger.warn('pin refused', id, why)
		return false
	}
	if (!drop.length && !add.length) return true
	step(dispatch, getState)
	if (drop.length) dispatch(unpinKeys(drop, { history: false }))
	if (add.length) dispatch(pinKeys(add, { history: false }))
	return true
}

/** Pinned objects among `ids` and their subtrees, with the presets that carry any of them. */
export const pinnedInDelete = (s, ids) => {
	const pinned = pinnedIds(s)
	const hit = [...gatherSubtreeIds(s.live.objects, ids)].filter(id => pinned.has(id))
	const presets = hit.length ? selectPresets(s).filter(p => hit.some(id => p.settings?.objects?.[id])) : []
	return { ids: hit, presets: presets.length }
}

/** A deleted pinned object leaves every preset too. */
export const cascadeDelete = ids => (dispatch, getState) => {
	const s = getState()
	if (!ids.length || !writesPresets(s)) return
	writePresets(dispatch, s, d => {
		const present = ids.filter(id => d.objects?.[id])
		if (present.length) recursivelyDeleteObjects(d, present)
	})
}
